import { useApplicantAPI, useRequest } from "@certn/api";
import {
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Space,
  Text,
  Modal,
  showToast,
  BasicButton,
} from "@certn/cascadia";
import { Divider, FileUpload, LinkButton } from "@certn/cascadia-react";
import { FileInfo } from "@certn/cascadia-react/components/upload/assets/utils";
import { DEFAULT_FILE_TYPES } from "@certn/utils";
import { t } from "i18next";
import { useContext, useState } from "react";
import { Trans } from "react-i18next";
import { useParams } from "react-router-dom";

import { DCOReviewContext } from "../DCOReviewContext";
import { DCOReviewPageLayout } from "../DCOReviewLayout";
import { MissingRequiredDocumentsModal } from "../MissingRequiredDocumentsModal";

export function DisputeResultsStep() {
  const { caseId } = useParams();

  const acicDisputeFormsDirectory =
    "acic_dispute_forms/" + caseId?.replace("CASE_AU_", "CC:");

  const { data, isLoading } = useApplicantAPI(
    `/api/internal/applicant/NCCHC_Dispute_Form_CertnCentric/presigned-read-url/` as "/api/internal/applicant/{documentname}/presigned-read-url/"
  );

  const request = useRequest();
  const [
    isMissingRequiredDocumentsModalVisible,
    setIsMissingRequiredDocumentsModalVisible,
  ] = useState(false);
  const { dispute, goToNextStep, goToPrevStep } = useContext(DCOReviewContext);
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([]);

  const submitDispute = async () => {
    handleDCOReviewResponse()
      .then(() => {
        goToNextStep();
      })
      .catch((e) => {
        console.error("Error submitting DCO review response:", e);
        showToast(t("Something went wrong, try again"), {
          type: "error",
        });
      });
  };

  const handleDCOReviewResponse = async () => {
    const url = `/api/internal/applicant/case/${caseId}/australian-ncchc/dco-review-response/`;
    await request(url, {
      method: "POST",
      body: JSON.stringify({
        dispute,
      }),
    });
  };

  return (
    <DCOReviewPageLayout
      onComplete={submitDispute}
      nextDisabled={uploadedFiles.length === 0}
    >
      <Text style="form/body-primary" color="text-base">
        <Trans>
          You have indicated that you wish to dispute the results of your
          recently conducted{" "}
          <strong>
            Nationally Coordinated Criminal History Check (NCCHC).
          </strong>{" "}
          Complete the steps below to dispute your results.
        </Trans>
      </Text>
      <Space />
      <Text style="form/section-heading" color="text-base">
        <Trans>Step 1: Download your Dispute Form</Trans>
      </Text>
      <LinkButton
        testID="dispute-pdf-download"
        onPress={() => {
          window.open(data?.presigned_url, "_blank");
        }}
        isLoading={isLoading}
        rightIcon="open_in_new"
      >
        <Text style="form/body-primary-link" color="text-base">
          <Trans>dispute-form.pdf</Trans>
        </Text>
      </LinkButton>
      <Divider />
      <Text style="form/section-heading" color="text-base">
        <Trans>Step 2: Complete your dispute form</Trans>
      </Text>
      <Text style="form/body-secondary" color="text-base">
        <Trans>
          Complete the form, providing us with information relevant to your
          dispute.
        </Trans>
      </Text>

      <Divider />
      <Text style="form/section-heading" color="text-base">
        <Trans>Step 3: Upload your completed dispute form</Trans>
      </Text>
      <Text style="form/body-secondary" color="text-base">
        <Trans>
          {" "}
          Upload your completed dispute form and any supporting documents to
          help verify your claim.
        </Trans>
      </Text>
      <FileUpload
        files={uploadedFiles}
        setFiles={(files) => {
          setUploadedFiles(files);
          if (files.length === 0) {
            setIsMissingRequiredDocumentsModalVisible(true);
          }
        }}
        allowedFileTypes={DEFAULT_FILE_TYPES}
        requestUrl="/api/internal/applicant/generate-presigned-url/"
        bucketDirectory={acicDisputeFormsDirectory}
        testID="dco-review-file-upload"
        multiple
        maxSizeBytes={10 * 1024 * 1024} // 10MB limit
        triggerButton={
          <BasicButton size="small" leftIcon="add">
            {t("Add Document")}
          </BasicButton>
        }
        caseId={caseId}
        uploadType="DISPUTE_DOCUMENT"
        useTrueFileName
      />
      <Divider />
      <ButtonGroup justify="end">
        <GhostButton leftIcon="chevron_left" onPress={goToPrevStep}>
          {t("Go back")}
        </GhostButton>
        <PrimaryButton
          onPress={submitDispute}
          testID="next-step-complete-dco-review-button"
          isDisabled={uploadedFiles.length === 0}
        >
          {t("Submit Dispute")}
        </PrimaryButton>
      </ButtonGroup>
      <Modal
        testID="missing-required-docs-modal"
        isOpen={isMissingRequiredDocumentsModalVisible}
      >
        <MissingRequiredDocumentsModal
          onClose={() => {
            setIsMissingRequiredDocumentsModalVisible(false);
          }}
        />
      </Modal>
    </DCOReviewPageLayout>
  );
}
